import { useCallback, useMemo, useState, useEffect } from "react";
import { Collapse, Button, Drawer, Badge } from "antd";
import { cancelIcon, chevronClose, chevronOpen } from "svgs";
import { FilterOutlined, CloseOutlined, CheckOutlined } from "@ant-design/icons";

import { Checkbox } from "components/base";
import { CheckboxPanelList } from "./sub-components";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "store";
import {
  onChangeVehiclesFilters,
  onChangeRootVehiclesFilters,
} from "store/actions";
import { getIndeterminate, getAllCheckedStatus } from "utils/helpers";


export default function VehiclesFilter() {
  const dispatch = useDispatch();
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);

  const {
    filters: { original: originalFilters, updated: updatedFilters },
  } = useSelector<AppState, AppState["vehicles"]>((state) => state.vehicles);

  const isAllChecked = useMemo(
    () => getAllCheckedStatus(updatedFilters, originalFilters),
    [updatedFilters, originalFilters]
  );
  const isIndeterminate = useMemo(
    () => getIndeterminate(updatedFilters, originalFilters),
    [updatedFilters, originalFilters]
  );
  const { userType = 1 } = useSelector((state: AppState) => state.common);

  // Count active filters
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    Object.values(updatedFilters).forEach((filterArray: any[]) => {
      count += filterArray.length;
    });
    return count;
  }, [updatedFilters]);

  // Handle responsive
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 992);
      // Close drawer when switching to desktop
      if (window.innerWidth >= 992) {
        setMobileDrawerOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const onChange = useCallback(
    (name: string, value: any, type: "list" | "all") => {
      console.log(`Name: ${name}, value: ${value}, type: ${type}`);
      dispatch(onChangeVehiclesFilters(name, value, type));
    },
    [dispatch]
  );

  const onRootChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(onChangeRootVehiclesFilters(event.target.checked));
    },
    [dispatch]
  );

  const onClearAll = useCallback(() => {
    dispatch(onChangeRootVehiclesFilters(false));
  }, [dispatch]);

  const handleOpenDrawer = useCallback(() => {
    setMobileDrawerOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setMobileDrawerOpen(false);
  }, []);

  const handleApplyFilters = useCallback(() => {
    setMobileDrawerOpen(false);
  }, []);

  // Filter content - reused in both desktop and mobile
  const filterContent = (
    <Collapse
      className="__common-collapse"
      ghost
      expandIconPosition="right"
      expandIcon={({ isActive }) => (isActive ? chevronOpen : chevronClose)}
      defaultActiveKey={Object.keys(originalFilters)}
    >
      {Object.entries(originalFilters)
        .filter(
          ([key]) => (userType <= 2 || (userType == 8 && key !== "Model") || (userType == 6 && key !== "Model")) || (key !== "Client" && key !== "Model")
        )
        .map(([key, row]) => (
          <CheckboxPanelList
            header={""}
            key={key}
            name={key}
            options={row}
            value={updatedFilters[key].map((filter: any) => filter.value)}
            onChange={onChange}
            search={key === "Client"}
          />
        ))}
    </Collapse>
  );

  return (
    <>
      {/* Desktop Filter Sidebar Content */}
      <div className="common-filter vehicles-filter-desktop">
        <div className="__filter-header">
          <div className="all-options">
            <span className="filter-text">Filters</span>
            {activeFiltersCount > 0 && (
              <Badge count={activeFiltersCount} className="filter-badge" />
            )}
            <Button
              className="clear-all-btn"
              icon={cancelIcon}
              type="link"
              danger
              onClick={onClearAll}
            >
              Clear All
            </Button>
          </div>
        </div>
        {filterContent}
      </div>

      {/* Mobile Floating Action Button */}
      {isMobile && (
        <div className="mobile-filter-trigger">
          <Button
            type="primary"
            icon={<FilterOutlined />}
            onClick={handleOpenDrawer}
            className="filter-fab-btn"
          >
            <span className="fab-text">Filters</span>
            {activeFiltersCount > 0 && (
              <span className="fab-badge">{activeFiltersCount}</span>
            )}
          </Button>
        </div>
      )}

      {/* Mobile Filter Drawer */}
      <Drawer
        title={
          <div className="drawer-title">
            <span>Filters</span>
            {activeFiltersCount > 0 && (
              <Badge count={activeFiltersCount} className="title-badge" />
            )}
          </div>
        }
        placement="right"
        onClose={handleCloseDrawer}
        visible={mobileDrawerOpen}
        className="vehicles-filter-drawer"
        width={300}
        bodyStyle={{ padding: 0 }}
        footer={
          <div className="drawer-footer">
            <Button onClick={onClearAll} className="clear-btn">
              Clear All
            </Button>
            <Button
              type="primary"
              onClick={handleApplyFilters}
              icon={<CheckOutlined />}
              className="apply-btn"
            >
              Apply Filters
            </Button>
          </div>
        }
      >
        <div className="drawer-filter-content">{filterContent}</div>
      </Drawer>
    </>
  );
}